import { Banner, BodyText, Spinner, Stack } from '@servicetitan/design-system';
import { MessageSessionType } from '../../types/message';
import MessageSession from '../MessageSession/MessageSession';
import { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

interface MessageThreadProps {
  sessions: MessageSessionType[];
  sessionId: string;
  onFetchMoreMessages: () => void;
}

/**
 * Renders a message thread component.
 * This component is responsible for rendering all of the messages from the sessions that has been loaded.
 * Scrolling to the top will trigger the `onFetchMoreMessages` function to fetch more messages.
 *
 * @component
 * @param {Array} sessions - The array of message sessions.
 * @param {string} sessionId - The ID of the current session.
 * @param {Function} onFetchMoreMessages - The function to fetch more messages.
 */
const MessageThread = ({
  sessions,
  sessionId,
  onFetchMoreMessages,
}: MessageThreadProps) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  const { ref } = useIntersectionObserver({
    onChange: (isIntersecting: boolean) => {
      if (isIntersecting) {
        onFetchMoreMessages();
        setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
          setHasError(true);
        }, 2_000);
      }
    },
    threshold: 1,
  });

  useEffect(() => {
    // Scroll to bottom on initial render
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'instant' });
  }, []);

  return (
    <>
      {hasError && (
        <Banner
          className='m-b-0'
          icon
          status='critical'
          title={'Failed to load messages'}
          primaryActionName='Retry'
          onPrimaryActionClick={() => {
            setHasError(false);
            setIsLoading(true);
            setTimeout(() => {
              setIsLoading(false);
            }, 2_000);
          }}
        />
      )}
      <div className='p-y-2 p-x-3 flex-grow-1 of-y-auto'>
        <div ref={ref} />
        {isLoading && (
          <Stack
            className='m-y-3'
            direction='column'
            spacing={1}
            alignItems='center'
          >
            <Spinner size='small' />
            <BodyText className='c-neutral-200'>Loading</BodyText>
          </Stack>
        )}
        <Stack direction='column' spacing={2}>
          {sessions.map((session) => (
            <MessageSession
              key={session.id}
              session={session}
              isCurrentSession={session.id === sessionId}
            />
          ))}
        </Stack>
        <div ref={endOfMessagesRef} />
      </div>
    </>
  );
};

export default MessageThread;
